import { useFetchData } from "@/hooks/useFetchData";
import { calculateFundingTotals } from "@/libs/utils";
import React, { useState } from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
const FundingPie = ({
  projectsByPp,
  carrier,
  devises,
  devise,
  type,
  className,
}) => {
  const { fetchData } = useFetchData();
  const [totals, setTotals] = useState({
    obtained: 0,
    fees: 0,
    contributions: 0,
    desired: 0,
  });
  const [loading, setLoading] = useState(false);
  React.useEffect(() => {
    (async () => {
      setLoading(true);

      if (!projectsByPp || Object.keys(projectsByPp).length === 0) {
        setLoading(false);
        return;
      }

      type Program = {
        projects: any[]; // ou un type spécifique au projet si tu l’as
      };

      const allProjects = Object.values(
        projectsByPp as Record<string, Program>
      ).flatMap((p) => p.projects || []);

      // Prépare les promesses SANS les await pour qu'elles s'exécutent en parallèle
      const fundingPromise = calculateFundingTotals(allProjects);
      const fetchReportPromise = fetchData(
        `${import.meta.env.VITE_API_ENDPOINT}/report/programs${
          carrier ? `?user=${carrier}&context=program` : "?context=program"
        }`
      );

      // On attend les deux résultats en parallèle
      const [fundingTotals, reportData] = await Promise.all([
        fundingPromise,
        fetchReportPromise,
      ]);

      // On déstructure le résultat de calculateFundingTotals
      const { totalObtained, fees, callContributions, totalDesired } =
        fundingTotals;
      setTotals({
        obtained: totalObtained,
        fees: fees,
        contributions: callContributions,
        desired: totalDesired,
      });
      setLoading(false);
    })();
  }, []);

  const formatCurrency = (value) => {
    return value.toLocaleString("fr-FR") + " " + devise;
  };

  const dataTotal = [
    {
      name: "Total",
      value: totals.obtained + totals.contributions,
      description:
        "Coût total projet (CTP) = " +
        formatCurrency(totals.obtained + totals.contributions),
    },
  ];

  const data = [
    {
      name: "CO",
      value: totals.obtained,
      description:
        "Contributions déjà obtenues (CO) = " + formatCurrency(totals.obtained),
    },
    {
      name: "FS",
      value: totals.contributions,
      description:
        "Financements souhaités (FS) = " + formatCurrency(totals.desired),
    },
  ];

  const data3 = [
    {
      name: "CO",
      value: totals.obtained,
      description:
        "Contributions déjà obtenues (CO) = " + formatCurrency(totals.obtained),
    },
    {
      name: "AC",
      value: totals.contributions,
      description:
        "Appel à contributions (AC) = " + formatCurrency(totals.contributions),
    },
  ];

  const COLORS = ["#9dc95f", "#dd0000", "#aa0000"];
  const COLORS3 = ["#9dc95f", "#ff0000"];

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-background text-foreground p-2 border rounded shadow-md">
          <p className="text-[11px] m-0 whitespace-nowrap">
            {payload[0].payload.description}
          </p>
        </div>
      );
    }
    return <></>;
  };

  const CustomLabel = ({ name, value, cx, cy, midAngle, outerRadius }: any) => {
    const RADIAN = Math.PI / 180;
    const x = cx + (outerRadius + 20) * Math.cos(-midAngle * RADIAN);
    const y = cy + (outerRadius + 20) * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
        fontSize={8}
        fontWeight="normal"
        className="fill-foreground"
      >
        {formatCurrency(value)}
      </text>
    );
  };

  return (
    <div className={`${className}`}>
      <ResponsiveContainer width="100%" height="100%">
        {type === 2 ? (
          <PieChart width={730} height={250} className="chart-financement">
            <Pie
              data={dataTotal}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={25}
              fill="#fff"
              stroke="none"
              label={({
                cx,
                cy,
                midAngle,
                innerRadius,
                outerRadius,
                value,
              }) => {
                const RADIAN = Math.PI / 180;

                // Positionner le label exactement au centre de la portion
                const radius = (innerRadius + outerRadius) / 18; // Rayon médian
                const x = cx + radius * Math.cos(-midAngle * RADIAN);
                const y = cy + radius * Math.sin(-midAngle * RADIAN);

                return (
                  <text
                    x={x}
                    y={y}
                    fill="#878787"
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize={10}
                  >
                    {value > 1000
                      ? Math.round(value / 1000) + "K"
                      : formatCurrency(value)}
                  </text>
                );
              }}
            />
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={26}
              outerRadius={36}
              fill="#82ca9d"
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length]}
                />
              ))}
            </Pie>
            <Pie
              data={data3}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={37}
              outerRadius={47}
              fill="#82ca9d"
              label={<CustomLabel />}
              labelLine
            >
              {data3.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS3[index % COLORS3.length]}
                />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        ) : (
          <PieChart width={730} height={250} className="chart-financement">
            <Pie
              data={data3}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={37}
              outerRadius={47}
              fill="#82ca9d"
              label={<CustomLabel />}
              labelLine
            >
              {data3.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS3[index % COLORS3.length]}
                />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        )}
      </ResponsiveContainer>
    </div>
  );
};

export default FundingPie;
